import { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { AuthorityBadge } from "./authority-badge";
import type { ResolvedCitationData } from "./citation-link";

interface CitationCardProps {
  resolved: ResolvedCitationData;
  onViewSource: (sourceType: string, sourceId: string) => void;
  crossRefCount?: number;
  className?: string;
}

export function CitationCard({
  resolved,
  onViewSource,
  crossRefCount,
  className,
}: CitationCardProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={cn(
        "my-2 rounded-lg border border-dashed bg-muted/30 transition-all",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <AuthorityBadge level={resolved.authorityLevel} />
        <span className="text-xs text-muted-foreground truncate">
          {resolved.sourceName}
        </span>
      </button>

      {!collapsed && (
        <div className="border-t border-dashed px-3 pb-3 pt-2">
          <p className="text-sm leading-relaxed text-foreground">
            {resolved.contentPreview}
          </p>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                onViewSource(resolved.sourceType, resolved.sourceId)
              }
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              View source
              <ExternalLink className="h-3 w-3" />
            </button>
            {crossRefCount != null && crossRefCount > 0 && (
              <span className="text-xs text-muted-foreground">
                Used in {crossRefCount} finding{crossRefCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
